
import { InsightCard } from "./insights/InsightCard";
import { useInsights } from "@/hooks/use-insights";
import { Industry, OrganizationSize } from "@/types/assessment";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface LLMInsightsProps {
  assessmentId: string;
  assessmentData: {
    organizationDetails: {
      industry: Industry;
      size: OrganizationSize;
    };
    dimensionScores: Record<string, number>;
  };
}

export const LLMInsights = ({ assessmentId, assessmentData }: LLMInsightsProps) => {
  const { insights, loading, error, refreshInsights } = useInsights({ assessmentId, assessmentData });

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertTitle>Error generating insights</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>{error}</p>
          {error.includes("foreign key constraint") && (
            <p className="text-sm">This is likely because your assessment wasn't saved to the database correctly.</p>
          )}
          <Button 
            size="sm" 
            variant="outline" 
            onClick={refreshInsights} 
            className="mt-2 flex items-center"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <Alert className="mb-6">
        <AlertTitle>Using offline insights</AlertTitle>
        <AlertDescription>
          <p>We're currently using pre-generated insights based on your assessment data.</p>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={refreshInsights} 
            className="mt-2 flex items-center"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh Insights
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {insights.map((insight, index) => (
        <InsightCard key={index} insight={insight} />
      ))}
      <div className="text-xs text-gray-500 pt-2 border-t">
        Note: Confidence scores reflect AIMAA's certainty level in providing these insights based on your assessment data and industry patterns.
      </div>
    </div>
  );
};
